import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FormProvider, useForm } from 'react-hook-form'
import * as datefns from 'date-fns'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import { IssueTypeField } from './IssueTypeField'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import CircularProgress from '@mui/material/CircularProgress'
import IssueApi from '../../../../api/IssueApi'
import { calculateIssueStatus } from '../../../../components/utils/calculateIssueStatus'
import { DeleteIssueConfirmationDialog } from './DeleteIssueConfirmationDialog'
import { LeavePageConfirmationDialog } from '../../../../components/dialogs/LeavePageConfirmationDialog'
import { FormAccordion } from './FormAccordion'
import { FormIssueEntity } from '../../types/FormIssueEntity'
import { PressReleasesExtractionsContext } from './PressReleasesExtractionsContext'
import PressReleasesExtractionsApi from '../../../../api/PressReleasesExtractionsApi'

const issueApi = new IssueApi()
const pressReleasesExtractionsApi = new PressReleasesExtractionsApi()

const fixDates = (issue) => {
    const dateFields = [
        'exRightsDate',
        'generalMeetingDate',
        'incRightsDate',
        'ipoTradingDate',
        'recordDate',
        'rightsEndDate',
        'rightsStartDate',
        'subEndDate',
        'subStartDate',
    ]
    dateFields.forEach((field) => {
        if (issue[field]) {
            // NOTE! This method and formatting is hack! Some more robust solution should be found.

            let date = new Date(issue[field])
            // if the time of day is after 12:00, set the date to the next day
            if (date.getHours() >= 12) {
                date = datefns.addDays(date, 1)
            }
            // replace date with yyyy-mm-dd string
            issue[field] = datefns.format(date, 'yyyy-MM-dd')
        }
    })
    return issue
}

export function IssueForm({
    issue,
    reFetchData,
}: {
    issue: FormIssueEntity
    reFetchData: (issue: FormIssueEntity) => Promise<void>
}) {
    const [lastSaved, setLastSaved] = useState<Date | null>(null)
    const [isFormDirty, setIsFormDirty] = useState(false)
    const [isCustomFormDirty, setIsCustomFormDirty] = useState(false)
    const [savingPossible, setSavingPossible] = useState(true)
    // Shared toggle for showing AI hints in all sections that opt in via context.
    const [showSuggestionDatapoints, setShowSuggestionDatapoints] = useState(false)
    const [suggestionImportantDates, setSuggestionImportantDates] = useState<Record<string, string | null>>({})
    const [suggestionOfferingTerms, setSuggestionOfferingTerms] = useState<Record<string, number | string | null>>({})
    const [suggestionOfferingOutcome, setSuggestionOfferingOutcome] = useState<Record<string, number | string | null>>({})
    const [suggestionProvenanceByField, setSuggestionProvenanceByField] = useState<
        Record<string, { sourceUrl: string | null; sourcePages: number[]; extractionId: string | null }>
    >({})
    const latestFetchRequestIdRef = React.useRef(0)
    const [votesDisableSave, setVotesDisableSave] = useState({})
    const [saving, setSaving] = useState(false)
    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false)
    const navigate = useNavigate()

    const formMethods = useForm({ defaultValues: issue })
    const extractAdvisorFormData = (advisor: Record<string, unknown>) => {
        const { id, name, type } = advisor
        return { id, name, type }
    }

    const sources = formMethods.watch('sources')
    const sourceUrlsKey = (sources ?? [])
        .map((source) => source.sourceUrl ?? '')
        .filter(Boolean)
        .sort()
        .join('|')

    const handleVoteSavingDisabled = useCallback(
        (field: string, disable: boolean) => {
            if (disable !== votesDisableSave[field]) {
                const newVotesDisableSave = { ...votesDisableSave, [field]: disable }
                setVotesDisableSave(newVotesDisableSave)
                const okToSave = !Object.values(newVotesDisableSave).some((v) => v)
                setSavingPossible(okToSave)
            }
        },
        [votesDisableSave],
    )

    useEffect(() => {
        formMethods.reset({ ...issue })
    }, [issue])

    /* API call: Whenever the issue changes, fetch the corresponding AI extractions.
    Useeffect based on issue.id, so it runs whenever a different issue is loaded
    */
    useEffect(() => {
        if (!issue?.id) {
            setSuggestionImportantDates({})
            setSuggestionOfferingTerms({})
            setSuggestionOfferingOutcome({})
            setSuggestionProvenanceByField({})
            return
        }

        const fetchRequestId = latestFetchRequestIdRef.current + 1
        latestFetchRequestIdRef.current = fetchRequestId
        let isDisposed = false

        const fetchPressReleasesExtractions = async () => {
            try {
                const extractionResponse = await pressReleasesExtractionsApi.findImportantDatesByIssueId(issue.id)
                if (isDisposed || fetchRequestId !== latestFetchRequestIdRef.current) return

                setSuggestionImportantDates(extractionResponse?.importantDates || {})
                setSuggestionOfferingTerms(extractionResponse?.offeringTerms || {})
                setSuggestionOfferingOutcome(extractionResponse?.offeringOutcome || extractionResponse?.offering_outcome || {})
                setSuggestionProvenanceByField(extractionResponse?.provenanceByField || {})
            } catch (fetchError) {
                if (isDisposed || fetchRequestId !== latestFetchRequestIdRef.current) return
                console.error('Failed to load press release extractions for issue', issue.id, fetchError)
                setSuggestionImportantDates({})
                setSuggestionOfferingTerms({})
                setSuggestionOfferingOutcome({})
                setSuggestionProvenanceByField({})
            }
        }

        void fetchPressReleasesExtractions()

        return () => {
            isDisposed = true
        }
    }, [issue?.id, sourceUrlsKey])

    const handleSave = async (data) => {
        if (saving) return
        if (!savingPossible) return
        setSaving(true)
        const { share, ...rest } = data
        const issueToSave = {
            shareId: share?.id || issue.shareId,
            ...rest,
            id: issue.id,
        }
        issueToSave.advisors = formMethods.getValues('advisors')?.map(extractAdvisorFormData) || []
        // replace all '' with null
        Object.keys(issueToSave).forEach((key) => {
            if (issueToSave[key] === '') issueToSave[key] = null
        })

        if (issueToSave.noOverAllotment) {
            issueToSave.overAllotmentSize = null
            issueToSave.overAllotmentSizeSecondary = null
            issueToSave.overAllotmentAllocation = null
            issueToSave.overAllotmentAllocationSecondary = null
        }

        if (issueToSave.noSecondary) {
            issueToSave.secondaryOffering = null
            issueToSave.overAllotmentSizeSecondary = null
            issueToSave.secondarySub = null
            issueToSave.overAllotmentAllocationSecondary = null
        }

        const unwantedFields = [
            'companyId',
            'companyName',
            'companyCountryCode',
            'companyRegistrationNumber',
            'createdAt',
            'currency',
            'firstFinancialAdvisorId',
            'firstFinancialAdvisorName',
            'hasWarrants',
            'instrumentId',
            'linkedIssues',
            'marketplaceName',
            'numFinancialAdvisors',
            'numWarrants',
            'numberOfSharesBeforePublicationDate',
            'offeredSize',
            'publicationClosePrice',
            'publicationDate',
            'rightsInstrumentName',
            'sector1',
            'sector2',
            'sector3',
            'shareName',
            'shareNameForIssue',
            'sharePrice',
            'bonusIssues',
            'shareSplits',
            'symbol',
            'termsDate',
            'tradesStats',
            'unitInstrumentName',
            'unitInstrumentSymbol',
            'unitIsTrading',
            'unitSubPriceSek',
            'updatedAt',
            'warrants',
            'newInstrument',
            'outcomeDate',
            'preMoney',
            'isInWatchList',
            'instrumentName',
            'shareNameMetadata',
        ]
        unwantedFields.forEach((field) => delete issueToSave[field])
        fixDates(issueToSave)

        const issueStatus = calculateIssueStatus(issueToSave)
        issueToSave.statusHideUntil = issueStatus.hideUntil
        issueToSave.statusStage = issueStatus?.stage || null
        issueToSave.statusComplete = issueStatus ? issueStatus.status === 'complete' : null
        issueToSave.statusMissingFields = issueStatus?.missingFields?.join(',') || null
        const publishedAt = issueToSave.publishedAt ? new Date(issueToSave.publishedAt) : new Date()
        issueToSave.publishedAt = datefns.format(publishedAt, 'yyyy-MM-dd HH:mm:ss') // set publishedAt if not set
        const updatedIssue = await issueApi.update(issueToSave.id, issueToSave)
        if (updatedIssue) {
            void reFetchData(updatedIssue)
            setLastSaved(new Date())
        } else {
            console.log('error result: no updated issue')
        }
        setIsFormDirty(false)
        setIsCustomFormDirty(false)
        setSaving(false)
    }
    const handleDeleteIssue = async () => {
        if (saving) return
        setSaving(true)
        const allDeleted = await issueApi.delete(issue.id)
        console.log({ allDeleted })
        navigate('/admin/issues')
        setSaving(false)
    }

    const hasPublicationSource = (sources ?? []).some((source) => source.sourceType === 'Publication')

    useEffect(() => {
        handleVoteSavingDisabled('sources', !hasPublicationSource) // disable saving if there is no publication source
    }, [hasPublicationSource, handleVoteSavingDisabled])

    const dirtyFormFields = Object.keys(formMethods.formState.dirtyFields)

    useEffect(() => {
        if (dirtyFormFields.length > 0) {
            setIsFormDirty(true)
        } else {
            setIsFormDirty(false)
        }
    }, [dirtyFormFields])

    return (
        <FormProvider {...formMethods}>
            <PressReleasesExtractionsContext.Provider
                value={{
                    showSuggestionDatapoints,
                    setShowSuggestionDatapoints,
                    suggestionImportantDates,
                    suggestionOfferingTerms,
                    suggestionOfferingOutcome,
                    suggestionProvenanceByField,
                }}
            >
                <DeleteIssueConfirmationDialog
                    open={deleteConfirmationOpen}
                    onClose={() => {
                        setDeleteConfirmationOpen(false)
                    }}
                    onDelete={handleDeleteIssue}
                />
                <LeavePageConfirmationDialog isDirty={isFormDirty || isCustomFormDirty} />
                <Grid sx={{ mb: 2 }} container spacing={1} direction='row'>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant='h6'>Aktie: {issue.shareName}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <IssueTypeField issue={issue} />
                    </Grid>
                    <Grid size={{ xs: 12, md: 1 }} mt={2}>
                        <Button
                            variant='outlined'
                            color='warning'
                            fullWidth
                            disabled={saving}
                            onClick={() => setDeleteConfirmationOpen(true)}
                        >
                            Radera
                        </Button>
                    </Grid>
                    <Grid size={{ xs: 12, md: 1 }} mt={2}>
                        <Stack sx={{ textAlign: 'center' }}>
                            <Button
                                variant='contained'
                                color='primary'
                                fullWidth
                                onClick={formMethods.handleSubmit(handleSave)}
                                disabled={saving || !savingPossible}
                            >
                                Spara {saving && <CircularProgress size={24} />}
                            </Button>
                            <Typography variant='caption' color='textSecondary'>
                                {lastSaved && datefns.format(lastSaved, 'H:mm:ss')}
                            </Typography>
                        </Stack>
                    </Grid>
                    <Grid size={{ xs: 12 }} mt={2}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={showSuggestionDatapoints}
                                    onChange={(event) => setShowSuggestionDatapoints(event.target.checked)}
                                />
                            }
                            // This controls visibility only; it does not mutate any form values.
                            label='Visa förslag från AI'
                        />
                    </Grid>
                </Grid>
                <FormAccordion voteDisableSave={handleVoteSavingDisabled} setIsCustomFormDirty={setIsCustomFormDirty} />
            </PressReleasesExtractionsContext.Provider>
        </FormProvider>
    )
}
